/**
 * InvoiceListPage
 * List and manage invoices with filters, pagination, and inline actions
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SelectChangeEvent,
} from '@mui/material';
import {
  Refresh,
  Visibility,
  Lock,
  LockOpen,
  Payment,
  Cancel,
  EventBusy,
  FileDownload,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  InlineFilterBar,
  InlineSelect,
} from '../../components';
import type { ActiveChip } from '../../hooks/useFilterState';
import { invoiceService, Invoice, InvoiceFilters } from '../../services/invoiceService';
import { pode } from '../../authorization/pode';
import { useAcao } from '../../authorization/useVeredito';
import { ActionButton, RouteActionButton } from '../../components/common';
import {
  formatCurrency,
  getAvailableYears,
  getAvailableMonths,
  STATUS_LABELS,
  STATUS_COLORS,
  TYPE_LABELS,
  TYPE_COLORS,
  formatPeriod,
} from '../../utils/financialHelpers';
import { InvoiceCreationFailuresBlock } from './InvoiceCreationFailuresBlock';

const TYPE_OPTIONS = [
  { value: 'SERVICE', label: 'Serviço' },
  { value: 'PLATFORM', label: 'Plataforma' },
];

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Aberta' },
  { value: 'CLOSED', label: 'Fechada' },
  { value: 'PAID', label: 'Paga' },
  { value: 'CANCELLED', label: 'Cancelada' },
  { value: 'OVERDUE', label: 'Vencidas' },
];

const isOverdue = (invoice: Invoice): boolean =>
  invoice.status === 'CLOSED' &&
  Boolean(invoice.dueDate) &&
  new Date(invoice.dueDate as string) < new Date(new Date().toISOString().slice(0, 10));

const columns: DataTableColumn<Invoice>[] = [
  {
    id: 'period',
    label: 'Período',
    primary: true,
    render: (invoice) => formatPeriod(invoice.referenceMonth, invoice.referenceYear),
  },
  {
    id: 'type',
    label: 'Tipo',
    render: (invoice) => (
      <Chip
        label={TYPE_LABELS[invoice.type] || invoice.type}
        color={TYPE_COLORS[invoice.type] || 'default'}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (invoice) => invoice.company?.tradeName || invoice.company?.legalName || '-',
  },
  {
    id: 'provider',
    label: 'Prestador',
    render: (invoice) => invoice.provider?.tradeName || invoice.provider?.legalName || '-',
  },
  {
    id: 'totalAmount',
    label: 'Valor Total',
    align: 'right',
    render: (invoice) => formatCurrency(invoice.totalAmount),
  },
  {
    id: 'status',
    label: 'Status',
    render: (invoice) => (
      <>
        <Chip
          label={STATUS_LABELS[invoice.status] || invoice.status}
          color={STATUS_COLORS[invoice.status] || 'default'}
          size="small"
        />
        {isOverdue(invoice) && (
          <Chip label="Vencida" size="small" color="error" sx={{ ml: 0.5 }} />
        )}
      </>
    ),
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

const InvoiceListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const fecharFatura = useAcao('invoices.manage', 'Fechar fatura');
  const reabrirFatura = useAcao('invoices.manage', 'Reabrir fatura');
  const marcarPaga = useAcao('invoices.manage', 'Marcar como paga');
  const cancelarFatura = useAcao('invoices.manage', 'Cancelar fatura');

  // Data states
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>(searchParams.get('type') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState<number | ''>('');

  const [exportLoading, setExportLoading] = useState(false);

  // Close month dialog
  const [closeMonthOpen, setCloseMonthOpen] = useState(false);
  const [closeMonthMonth, setCloseMonthMonth] = useState<number>(new Date().getMonth() + 1);
  const [closeMonthYear, setCloseMonthYear] = useState<number>(new Date().getFullYear());
  const [closeMonthCount, setCloseMonthCount] = useState<number | null>(null);
  const [closeMonthLoading, setCloseMonthLoading] = useState(false);
  const [closeMonthCountLoading, setCloseMonthCountLoading] = useState(false);

  const fetchOpenCount = useCallback(async (month: number, year: number) => {
    setCloseMonthCountLoading(true);
    try {
      const result = await invoiceService.getInvoices({
        status: 'OPEN',
        referenceMonth: month,
        referenceYear: year,
        limit: 1,
      });
      setCloseMonthCount(result.pagination.total);
    } catch {
      setCloseMonthCount(null);
    } finally {
      setCloseMonthCountLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters: InvoiceFilters = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (typeFilter) filters.type = typeFilter;
      if (statusFilter === 'OVERDUE') {
        filters.overdue = true;
      } else if (statusFilter) {
        filters.status = statusFilter;
      }
      if (yearFilter) filters.referenceYear = yearFilter;
      if (monthFilter !== '') filters.referenceMonth = monthFilter;

      const response = await invoiceService.getInvoices(filters);
      setInvoices(response.data);
      setTotal(response.pagination.total);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar faturas';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, typeFilter, statusFilter, yearFilter, monthFilter, enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Action handlers
  const handleAction = async (
    id: number,
    action: 'close' | 'reopen' | 'pay' | 'cancel',
    confirmMsg: string,
  ) => {
    if (!window.confirm(confirmMsg)) return;

    setActionLoading(id);
    try {
      switch (action) {
        case 'close':
          await invoiceService.closeInvoice(id);
          enqueueSnackbar('Fatura fechada com sucesso!', { variant: 'success' });
          break;
        case 'reopen':
          await invoiceService.reopenInvoice(id);
          enqueueSnackbar('Fatura reaberta com sucesso!', { variant: 'success' });
          break;
        case 'pay':
          await invoiceService.markAsPaid(id);
          enqueueSnackbar('Fatura marcada como paga!', { variant: 'success' });
          break;
        case 'cancel':
          await invoiceService.cancelInvoice(id);
          enqueueSnackbar('Fatura cancelada!', { variant: 'success' });
          break;
      }
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao executar ação';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      const filters: InvoiceFilters = { referenceYear: yearFilter };
      if (typeFilter) filters.type = typeFilter;
      if (monthFilter !== '') filters.referenceMonth = monthFilter as number;
      if (statusFilter === 'OVERDUE') {
        filters.overdue = true;
      } else if (statusFilter) {
        filters.status = statusFilter;
      }
      await invoiceService.exportCsv(filters);
    } catch (err: unknown) {
      enqueueSnackbar(
        err instanceof Error ? err.message : 'Erro ao exportar faturas',
        { variant: 'error' },
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenCloseMonth = () => {
    setCloseMonthOpen(true);
    fetchOpenCount(closeMonthMonth, closeMonthYear);
  };

  const handleCloseMonthDialogClose = () => {
    setCloseMonthOpen(false);
    setCloseMonthCount(null);
  };

  const handleCloseMonthMonthChange = (e: SelectChangeEvent<number>) => {
    const month = Number(e.target.value);
    setCloseMonthMonth(month);
    fetchOpenCount(month, closeMonthYear);
  };

  const handleCloseMonthYearChange = (e: SelectChangeEvent<number>) => {
    const year = Number(e.target.value);
    setCloseMonthYear(year);
    fetchOpenCount(closeMonthMonth, year);
  };

  const handleCloseMonthConfirm = async () => {
    setCloseMonthLoading(true);
    try {
      const result = await invoiceService.closeMonth(closeMonthMonth, closeMonthYear);
      enqueueSnackbar(`${result.closed} faturas fechadas com sucesso`, { variant: 'success' });
      handleCloseMonthDialogClose();
      loadData();
    } catch (err: unknown) {
      enqueueSnackbar(
        err instanceof Error ? err.message : 'Erro ao fechar faturas do mês',
        { variant: 'error' },
      );
    } finally {
      setCloseMonthLoading(false);
    }
  };

  const months = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  // The inline bar's selects work with string values; the filter state itself
  // stays numeric (year) or numeric-or-empty (month) to match the API contract.
  const defaultYear = new Date().getFullYear();
  const yearOptions = getAvailableYears().map((y) => ({ value: String(y), label: String(y) }));
  const monthOptions = months.map((m) => ({ value: String(m.value), label: m.label }));

  const filterChips: ActiveChip[] = [];
  if (typeFilter) {
    filterChips.push({
      key: 'type',
      label: 'Tipo',
      value: TYPE_LABELS[typeFilter] || typeFilter,
      onDelete: () => { setTypeFilter(''); setPage(0); },
    });
  }
  if (statusFilter) {
    const statusLabel =
      statusFilter === 'OVERDUE' ? 'Vencidas' : STATUS_LABELS[statusFilter] || statusFilter;
    filterChips.push({
      key: 'status',
      label: 'Status',
      value: statusLabel,
      onDelete: () => { setStatusFilter(''); setPage(0); },
    });
  }
  if (yearFilter !== defaultYear) {
    filterChips.push({
      key: 'year',
      label: 'Ano',
      value: String(yearFilter),
      onDelete: () => { setYearFilter(defaultYear); setPage(0); },
    });
  }
  if (monthFilter !== '') {
    filterChips.push({
      key: 'month',
      label: 'Mês',
      value: months.find((m) => m.value === monthFilter)?.label ?? String(monthFilter),
      onDelete: () => { setMonthFilter(''); setPage(0); },
    });
  }

  const clearAllFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setYearFilter(defaultYear);
    setMonthFilter('');
    setPage(0);
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Financeiro', path: '/financial' },
        { label: 'Faturas' },
      ]}
      title="Faturas"
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={exportLoading ? <CircularProgress size={18} /> : <FileDownload />}
            disabled={exportLoading}
            onClick={handleExportCsv}
          >
            Exportar CSV
          </Button>
          <ActionButton
            acao="invoices.manage"
            rotulo="Fechar Mês"
            variant="outlined"
            color="warning"
            startIcon={<EventBusy />}
            onClick={handleOpenCloseMonth}
          >
            Fechar Mês
          </ActionButton>
          <Tooltip title="Atualizar lista">
            <span>
              <IconButton onClick={loadData} disabled={loading}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </>
      }
    >
        <InvoiceCreationFailuresBlock />

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <InlineFilterBar
            resultCount={total}
            resultNoun="faturas"
            chips={filterChips}
            onClearAll={clearAllFilters}
            loading={loading}
            actions={
              <RouteActionButton
                para="/financial/platform-fees"
                exige={pode('platform-fees.view')}
                variant="outlined"
              >
                Configurar Taxas
              </RouteActionButton>
            }
          >
            <InlineSelect
              label="Tipo"
              value={typeFilter}
              options={TYPE_OPTIONS}
              onChange={(v) => { setTypeFilter(v); setPage(0); }}
              emptyLabel="Todos"
              minWidth={140}
            />
            <InlineSelect
              label="Status"
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={(v) => { setStatusFilter(v); setPage(0); }}
              emptyLabel="Todos"
              minWidth={140}
            />
            <InlineSelect
              label="Ano"
              value={String(yearFilter)}
              options={yearOptions}
              onChange={(v) => { setYearFilter(Number(v)); setPage(0); }}
              minWidth={110}
            />
            <InlineSelect
              label="Mês"
              value={monthFilter === '' ? '' : String(monthFilter)}
              options={monthOptions}
              onChange={(v) => { setMonthFilter(v === '' ? '' : Number(v)); setPage(0); }}
              emptyLabel="Todos"
              minWidth={150}
            />
          </InlineFilterBar>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

      <DataTable
        columns={columns}
        rows={invoices}
        getRowId={(invoice) => invoice.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma fatura encontrada.',
          description:
            'As faturas são geradas pela plataforma a partir dos atendimentos das empresas do seu acesso.',
        }}
        onRowClick={(invoice) => navigate(`/financial/invoices/${invoice.id}`)}
        actions={[
          {
            icon: <Visibility fontSize="small" />,
            tooltip: 'Ver detalhes',
            onClick: (invoice) => navigate(`/financial/invoices/${invoice.id}`),
          },
          {
            icon: <Lock fontSize="small" />,
            color: 'info',
            onClick: (invoice) => {
              if (actionLoading === invoice.id) return;
              handleAction(invoice.id, 'close', 'Deseja fechar esta fatura?');
            },
            hidden: (invoice) => invoice.status !== 'OPEN',
            ...fecharFatura,
          },
          {
            icon: <LockOpen fontSize="small" />,
            color: 'warning',
            onClick: (invoice) => {
              if (actionLoading === invoice.id) return;
              handleAction(invoice.id, 'reopen', 'Deseja reabrir esta fatura?');
            },
            hidden: (invoice) => invoice.status !== 'CLOSED',
            ...reabrirFatura,
          },
          {
            icon: <Payment fontSize="small" />,
            color: 'success',
            onClick: (invoice) => {
              if (actionLoading === invoice.id) return;
              handleAction(invoice.id, 'pay', 'Deseja marcar esta fatura como paga?');
            },
            hidden: (invoice) => invoice.status !== 'CLOSED',
            ...marcarPaga,
          },
          {
            icon: <Cancel fontSize="small" />,
            color: 'error',
            onClick: (invoice) => {
              if (actionLoading === invoice.id) return;
              handleAction(
                invoice.id,
                'cancel',
                'Tem certeza que deseja cancelar esta fatura? Esta ação não pode ser desfeita.',
              );
            },
            hidden: (invoice) => invoice.status !== 'OPEN' && invoice.status !== 'CLOSED',
            ...cancelarFatura,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          rowsPerPageOptions: [10, 20, 50],
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

        {/* Close Month Dialog */}
        <Dialog open={closeMonthOpen} onClose={handleCloseMonthDialogClose} maxWidth="xs" fullWidth>
          <DialogTitle>Fechar Faturas do Mês</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Mês</InputLabel>
                <Select<number>
                  value={closeMonthMonth}
                  label="Mês"
                  onChange={handleCloseMonthMonthChange}
                >
                  {getAvailableMonths().map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Ano</InputLabel>
                <Select<number>
                  value={closeMonthYear}
                  label="Ano"
                  onChange={handleCloseMonthYearChange}
                >
                  {getAvailableYears().map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              {closeMonthCountLoading ? (
                <CircularProgress size={20} />
              ) : closeMonthCount === 0 ? (
                <Alert severity="info">Nenhuma fatura aberta neste período</Alert>
              ) : closeMonthCount !== null ? (
                <Alert severity="warning">
                  {closeMonthCount} {closeMonthCount === 1 ? 'fatura será fechada' : 'faturas serão fechadas'}
                </Alert>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMonthDialogClose}>Cancelar</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleCloseMonthConfirm}
              disabled={closeMonthLoading || closeMonthCountLoading || closeMonthCount === 0 || closeMonthCount === null}
            >
              {closeMonthLoading ? <CircularProgress size={20} /> : 'Confirmar Fechamento'}
            </Button>
          </DialogActions>
        </Dialog>
    </PageShell>
  );
};

export default InvoiceListPage;
